import type { ImgHTMLAttributes, FC, Ref } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import type { ThemeName } from "../../theme/types";
import { logoStyle, imageStyle } from "./LogoSmall.css";

export type LogoSmallProps = ImgHTMLAttributes<HTMLImageElement> &
  RecipeVariants<typeof logoStyle> & {
    theme: ThemeName;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLImageElement>;
  };

/**
 * WhiteLabelLogo component returns different SVG logos based on the theme name.
 */
export const LogoSmall: FC<LogoSmallProps> = ({
  theme,
  size = "m",
  className,
  ref,
  ...props
}) => {
  if (["light", "dark"].includes(theme)) {
    return null;
  }

  const getLogoSrc = (themeName: ThemeName): string | undefined => {
    switch (themeName) {
      case "munt":
      case "obvion":
        return `/assets/images/logos/small/${themeName}.png`;
      case "unive":
      case "deHypotheker":
      case "ing":
      case "rabobank":
      case "nationaleNederlanden":
      case "vanLanschotKempen":
      case "verenigingEigenHuis":
      case "quion":
      case "nibc":
      case "hypotheekvisie":
      case "vista":
      case "asr":
      case "anwb":
      case "eviVanLanschot":
      case "allianz":
      case "fnv":
      case "randstad":
      case "achmea":
      case "funda":
      case "nvm":
      case "abnamro":
      case "domek":
        return `/assets/images/logos/small/${themeName}.svg`;
      default:
        return undefined;
    }
  };

  const logoSrc = getLogoSrc(theme);

  if (!logoSrc) {
    return null;
  }

  const isPng = logoSrc.endsWith(".png");

  return (
    <div className={mergeClasses(logoStyle({ size }), className)}>
      <img
        ref={ref}
        className={imageStyle({ contain: isPng })}
        src={logoSrc}
        alt={`${theme} logo`}
        {...props}
      />
    </div>
  );
};
